import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * Vaha (Balance Scale Worksheet)
 *
 * Layout: 2x5 grid of balance scale exercises. Each cell shows a balance
 * with the left label drawn on it.
 *
 * Migrated from C# EduFrame.Single.Generators.Vaha
 */
export class Vaha implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const countX = 2;
    const countY = 5;
    const itemsPerPage = countX * countY;
    const margin = PdfUnit.FromMillimeters(5);

    const count = Math.min(opts.list.length, this.countMax());
    const items = opts.list.slice(0, count);

    const allLabelTexts = items.map((item) => item.left.getText() ?? '');

    const totalPages = Math.ceil(count / itemsPerPage);

    const { size: cellSize } = calcOrigin(countX, countY, margin, PdfSize.A4);
    const panWidth = cellSize.width * 0.4;
    const panHeight = cellSize.height * 0.35;

    const firstPage = pdf.addPage();
    const labelFont = firstPage.adjustFontMulti(
      allLabelTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(panWidth, panHeight)
    );

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
      const page = pageIndex === 0 ? firstPage : pdf.addPage();

      const { origin, size } = calcOrigin(countX, countY, margin, PdfSize.A4);

      const pageStartIndex = pageIndex * itemsPerPage;
      const pageEndIndex = Math.min(pageStartIndex + itemsPerPage, count);

      for (let x = 0; x < countX; x++) {
        for (let y = 0; y < countY; y++) {
          const index = pageStartIndex + x * countY + y;
          if (index >= pageEndIndex) continue;

          const item = items[index];

          const rect = new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          );

          const y1 = rect.y + rect.height * 0.7;
          const d1 = rect.width * 0.1;
          const d2 = rect.width * 0.05;

          const px0 = new PdfPoint(rect.centerX, y1);
          const px1 = new PdfPoint(rect.centerX - d2, y1 + d2);
          const px2 = new PdfPoint(rect.centerX + d2, y1 + d2);

          const p1 = new PdfPoint(rect.left + d1, y1);
          const p2 = new PdfPoint(rect.right - d1, y1);

          const r1 = new PdfRect(p1.x, y1 - panHeight, panWidth, panHeight);

          page.drawPolygon(GUtil.basePen, null, [px0, px1, px2]);

          page.drawLabel(item.left, { rect: r1, font: labelFont });

          page.drawLine(GUtil.basePen, p1, p2);

          page.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}
